import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { withTheme } from '@mui/styles';

import { v4 as uuidv4 } from 'uuid';
import clsx from 'clsx';
import { FormLabel, IconButton, Typography } from '@kep-ui-kit/ui-components';
import {
  Bold24Icon,
  Bulletlist24Icon,
  Image24Icon,
  Italic24Icon,
  Link24Icon,
  OrderedList24Icon,
  Underline24Icon,
} from '@kep-ui-kit/ui-icons';

import {
  getRichTextClass,
  RICH_TEXT_COMPONENT_NAME,
  RichTextClasses,
} from './rich-text-classes';
import { composeClasses } from './utils/compose-classes';
import { RangeStatic } from 'quill';

const icons = Quill.import('ui/icons');
icons.bold = null;
icons.italic = null;
icons.underline = null;
icons.color = null;
icons.image = null;
icons.link = null;
icons.bullet = null;
icons.list = null;

export enum ESupportedActions {
  Bold = 'Bold',
  Italic = 'Italic',
  Underline = 'Underline',
  List = 'List',
  Image = 'Image',
  Link = 'Link',
}

export const ALL_ACTIONS = [
  ESupportedActions.Bold,
  ESupportedActions.Italic,
  ESupportedActions.Underline,
  ESupportedActions.List,
  ESupportedActions.Image,
  ESupportedActions.Link,
];

export interface RichTextProps {
  wrapperId?: string;
  classes?: Partial<RichTextClasses>;
  label?: string;
  onChange?: (value: string) => void;
  errorMessage?: string;
  defaultValue?: string;
  readOnly?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  supportedActions?: ESupportedActions[];
  maxHeightInput?: number;
  contentField?: string;
}

const useUtilityClasses = (props: RichTextProps) => {
  const { classes } = props;
  const slots = {
    root: ['root'],
    richText: ['richText'],
    readOnly: ['readOnly'],
    error: ['error'],
    focused: ['focused'],
    toolbar: ['toolbar'],
  };
  return composeClasses(slots, getRichTextClass, classes);
};

const FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const StyledInputContainer = styled('div')(({ theme }: { theme?: any }) => ({
  width: '100%',
  display: 'inline-flex',
  flexDirection: 'column',
  position: 'relative',
}));

const StyledInputError = styled('label')(({ theme }: { theme?: any }) => ({
  color: theme.palette.error.main,
  marginTop: theme.spacing(1),
  display: 'block',
}));

const StyledInputWrapper = styled('div')(
  ({
    theme,
    focused,
    error,
    readonly,
    maxHeightInput,
  }: { theme?: any } & {
    focused: boolean;
    error?: boolean;
    readonly?: boolean;
    maxHeightInput?: number;
  }) => ({
    position: 'relative',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${
      readonly
        ? theme.palette.grey.A400
        : focused
        ? theme.palette.primary.main
        : error
        ? theme.palette.error.main
        : theme.palette.grey.A400
    }`,
    '& .ql-container.ql-snow': {
      ...(maxHeightInput && {
        maxHeight: `${maxHeightInput}px`,
        overflow: 'auto',
      }),
      fontFamily: theme.typography.fontFamily,
      border: 'none',
      '& .ql-editor': {
        padding: `0 ${theme.spacing(2)} ${theme.spacing(2)}`,
      },
      '& .ql-tooltip': {
        borderRadius: theme.shape.borderRadius,
        '& .ql-action, & .ql-remove': {
          color: theme.palette.primary.main,
          fontWeight: theme.typography.fontWeightBold,
        },
      },
    },
  })
);

const StyledToolBar = styled.div(({ theme }: { theme?: any }) => ({
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  '&.ql-snow.ql-toolbar': {
    border: 'none',
    '& button': {
      width: 'inherit',
      height: 'inherit',
      color: theme.palette.grey[100],
      marginRight: theme.spacing(1),
      '&.ql-active': {
        backgroundColor: theme.palette.grey[400],
      },
      '&:disabled': {
        color: theme.palette.grey.A400,
      },
    },
  },
}));

export const RichText = withTheme((props: RichTextProps) => {
  const {
    wrapperId,
    label,
    onChange,
    errorMessage,
    defaultValue,
    readOnly,
    onFocus,
    onBlur,
    supportedActions = ALL_ACTIONS,
    maxHeightInput,
    contentField,
  }: RichTextProps = props;
  const classes = useUtilityClasses(props);
  const [focused, setFocused] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [richTextUUID] = useState(uuidv4());
  const reactQuillRef = useRef<ReactQuill>(null);
  const [styleChange, setStyleChange] = useState(false);

  const handleChange = (content: string, delta: any, source: any) => {
    source === 'user' && onChange && onChange(content);
  };

  useEffect(() => {
    if (
      !reactQuillRef.current ||
      typeof reactQuillRef.current?.getEditor !== 'function'
    ) {
      return;
    }

    const quill = reactQuillRef.current.getEditor();
    const delta = quill.clipboard.convert(defaultValue as string);
    quill.setContents(delta, 'silent');
  }, []);

  const handleSelectionChange = (range: any) => {
    setHasSelection(!!range?.length);
  };

  const onFieldFocus = () => {
    setFocused(true);
    onFocus && onFocus();
  };

  const onFieldBlur = () => {
    setFocused(false);
    onBlur && onBlur();
  };

  const styleChangeHandler = useCallback(
    (style: string) => {
      if (
        !reactQuillRef.current ||
        typeof reactQuillRef.current?.getEditor !== 'function'
      ) {
        return;
      }

      if (styleChange) {
        setStyleChange(false);
        return;
      }

      const quill = reactQuillRef.current.getEditor();
      const selection = quill.getSelection() as RangeStatic;
      if (!selection) return;

      const hasStyle = quill.getFormat()[style];
      quill.formatText(
        selection.index,
        selection.length,
        style,
        !hasStyle,
        'user'
      );

      setStyleChange(true);
    },
    [styleChange]
  );

  const boldHandler = useCallback(() => {
    styleChangeHandler('bold');
  }, [styleChangeHandler]);

  const italicHandler = useCallback(() => {
    styleChangeHandler('italic');
  }, [styleChangeHandler]);

  const underlineHandler = useCallback(() => {
    styleChangeHandler('underline');
  }, [styleChangeHandler]);

  const RichTextToolBar = useMemo(() => {
    return (
      <StyledToolBar id={`toolbar`} className={classes.toolbar}>
        {supportedActions.includes(ESupportedActions.Bold) && (
          <IconButton
            variant={'borderless'}
            className="ql-bold"
            disabled={readOnly}
          >
            <Bold24Icon />
          </IconButton>
        )}
        {supportedActions.includes(ESupportedActions.Italic) && (
          <IconButton
            variant={'borderless'}
            className="ql-italic"
            disabled={readOnly}
          >
            <Italic24Icon />
          </IconButton>
        )}
        {supportedActions.includes(ESupportedActions.Underline) && (
          <IconButton
            variant={'borderless'}
            className="ql-underline"
            disabled={readOnly}
          >
            <Underline24Icon />
          </IconButton>
        )}
        {supportedActions.includes(ESupportedActions.List) && (
          <>
            <IconButton
              variant={'borderless'}
              className="ql-list"
              value="bullet"
              disabled={readOnly}
            >
              <Bulletlist24Icon />
            </IconButton>
            <IconButton
              variant={'borderless'}
              className="ql-list"
              value="ordered"
              disabled={readOnly}
            >
              <OrderedList24Icon />
            </IconButton>
          </>
        )}
        {supportedActions.includes(ESupportedActions.Image) && (
          <IconButton
            variant={'borderless'}
            className="ql-image"
            disabled={readOnly}
          >
            <Image24Icon />
          </IconButton>
        )}
        {supportedActions.includes(ESupportedActions.Link) && (
          <IconButton
            variant={'borderless'}
            className="ql-link"
            disabled={readOnly || !hasSelection}
          >
            <Link24Icon />
          </IconButton>
        )}
      </StyledToolBar>
    );
  }, [readOnly, hasSelection, classes, richTextUUID]);

  return (
    <StyledInputContainer
      id={`rich-text-container-${richTextUUID}`}
      className={clsx(
        classes.root,
        readOnly ? classes.readOnly : '',
        focused ? classes.focused : '',
        errorMessage ? classes.error : ''
      )}
    >
      {label && (
        <FormLabel controlID={RICH_TEXT_COMPONENT_NAME} label={label} />
      )}
      <StyledInputWrapper
        focused={focused}
        error={!!errorMessage}
        readonly={readOnly}
        id={wrapperId}
        maxHeightInput={maxHeightInput}
      >
        {RichTextToolBar}
        <ReactQuill
          ref={reactQuillRef}
          onFocus={onFieldFocus}
          onBlur={onFieldBlur}
          onChange={handleChange}
          onChangeSelection={handleSelectionChange}
          placeholder={defaultValue as string}
          modules={{
            toolbar: {
              container: `#toolbar`,
              handlers: {
                bold: boldHandler,
                italic: italicHandler,
                underline: underlineHandler,
              },
            },
          }}
          formats={FORMATS}
          readOnly={readOnly}
          className={classes.richText}
          bounds={`#rich-text-container-${richTextUUID}`}
          preserveWhitespace
          value={contentField as string}
        />
      </StyledInputWrapper>
      {errorMessage && (
        <Typography>
          <StyledInputError>{errorMessage}</StyledInputError>
        </Typography>
      )}
    </StyledInputContainer>
  );
});
